/**
 * CardDetailModal — Top-Level-Modal fuer Lead-Detail-Ansicht (Plan 03-02).
 *
 * Verantwortlich fuer:
 *  - Auf Stamm-Store-Flag `selectedLeadId` reagieren (Modal-Open/Close)
 *  - Frischen Lead per leadsApi.get(id) holen bei jedem Open
 *  - Tab-State (Stammdaten / Notizen+Tags / Status+Workflow / Aktivitaet)
 *  - Dirty-Check beim Backdrop-/X-Klick (window.confirm)
 *  - Deep-Link-Sync: URL `?lead=:id` <-> store.selectedLeadId (bidirektional)
 *
 * Polling pausiert automatisch wenn das Modal offen ist —
 * Store-Flag `isModalOpen` ist beim `selectLead(id)` gesetzt.
 *
 * CI-Farben: #1A1A1A (Text), #DAD9D9 (Border).
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@libero/core/components/ui/tabs';
import { useLeadBoard } from '../store/useLeadBoard';
import { leadsApi } from '../services/leadsApi';
import type { Lead } from '../types';
import StammdatenTab from './tabs/StammdatenTab';
import NotesAndTagsTab from './tabs/NotesAndTagsTab';
import StatusWorkflowTab from './tabs/StatusWorkflowTab';
import TimelineTab from './tabs/TimelineTab';
import TasksTab from './tabs/TasksTab';
import MailverlaufTab from './tabs/MailverlaufTab';

type TabKey = 'stammdaten' | 'notes' | 'status' | 'tasks' | 'mailverlauf' | 'timeline';

export default function CardDetailModal() {
  const selectedLeadId = useLeadBoard((s) => s.selectedLeadId);
  const selectLead = useLeadBoard((s) => s.selectLead);
  const tasksByLead = useLeadBoard((s) => s.tasksByLead);

  const [lead, setLead] = useState<Lead | null>(null);
  const [tab, setTab] = useState<TabKey>('stammdaten');
  const [dirty, setDirty] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Deep-Link Inbound: URL ?lead=:id → Store ────────────────────────────
  // Reagiert auf URL-Aenderungen (Browser-Back/Forward, Permalink).
  useEffect(() => {
    const leadParam = searchParams.get('lead');
    const idFromUrl = leadParam ? Number(leadParam) : null;
    if (
      idFromUrl !== null &&
      !Number.isNaN(idFromUrl) &&
      idFromUrl !== selectedLeadId
    ) {
      selectLead(idFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ── Deep-Link Outbound: Store → URL ?lead=:id ───────────────────────────
  // Setzt oder loescht URL-Param wenn selectedLeadId sich aendert.
  // `replace: true` damit kein zusaetzlicher History-Eintrag entsteht.
  useEffect(() => {
    const current = searchParams.get('lead');
    if (selectedLeadId == null && current != null) {
      const next = new URLSearchParams(searchParams);
      next.delete('lead');
      setSearchParams(next, { replace: true });
    } else if (
      selectedLeadId != null &&
      current !== String(selectedLeadId)
    ) {
      const next = new URLSearchParams(searchParams);
      next.set('lead', String(selectedLeadId));
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeadId]);

  // ── Lead frisch laden bei jedem Open ────────────────────────────────────
  useEffect(() => {
    if (selectedLeadId == null) {
      setLead(null);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLead(null);
    setLoadError(null);
    setTab('stammdaten');
    setDirty(false);
    leadsApi
      .get(selectedLeadId)
      .then((l) => {
        if (!cancelled) setLead(l);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setLoadError(e?.message ?? 'Lead konnte nicht geladen werden');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedLeadId]);

  const close = () => {
    if (dirty && !window.confirm('Ungespeicherte Aenderungen verwerfen?')) {
      return;
    }
    selectLead(null);
  };

  if (selectedLeadId == null) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={close}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-lg max-w-[1700px] w-[96vw] h-[92vh] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-4 border-b border-[#DAD9D9]">
          <h2 className="text-lg font-semibold text-[#1A1A1A]">
            {lead ? lead.vereinName : 'Lead wird geladen...'}
          </h2>
          <button
            type="button"
            onClick={close}
            className="text-gray-500 hover:text-[#1A1A1A] rounded p-1"
            aria-label="Schliessen"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {loadError && (
          <div className="m-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
            {loadError}
          </div>
        )}

        {!lead && !loadError && (
          <div className="p-6 text-sm text-gray-500">Lade Lead-Daten...</div>
        )}

        {lead && (
          <Tabs
            value={tab}
            onValueChange={(v) => setTab(v as TabKey)}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <TabsList className="border-b border-[#DAD9D9] px-4 bg-transparent rounded-none h-auto">
              <TabsTrigger value="stammdaten">Stammdaten</TabsTrigger>
              <TabsTrigger value="notes">Notizen + Tags</TabsTrigger>
              <TabsTrigger value="status">Status + Workflow</TabsTrigger>
              <TabsTrigger value="tasks">
                Tasks
                {(() => {
                  const open = (tasksByLead[lead.id] ?? []).filter(
                    (t) => !t.completedAt,
                  ).length;
                  return open > 0 ? ` (${open})` : '';
                })()}
              </TabsTrigger>
              <TabsTrigger value="mailverlauf">Mailverlauf</TabsTrigger>
              <TabsTrigger value="timeline">Aktivitaet</TabsTrigger>
            </TabsList>
            {/* TabsContent direkt unter Tabs — kein Wrapper-div dazwischen,
                sonst schluckt der wrapper die internen _value-Props der
                core/Tabs-Komponente (cloneElement-cascade ist nur 1-tief). */}
            <TabsContent value="stammdaten" className="flex-1 overflow-auto p-4">
              <StammdatenTab
                lead={lead}
                onSaved={(l) => setLead(l)}
                onDirty={setDirty}
              />
            </TabsContent>
            <TabsContent value="notes" className="flex-1 overflow-auto p-4">
              <NotesAndTagsTab
                lead={lead}
                onSaved={(l) => setLead(l)}
                onDirty={setDirty}
              />
            </TabsContent>
            <TabsContent value="status" className="flex-1 overflow-auto p-4">
              <StatusWorkflowTab
                lead={lead}
                onSaved={(l) => setLead(l)}
                onDeleted={() => selectLead(null)}
              />
            </TabsContent>
            <TabsContent value="tasks" className="flex-1 overflow-auto p-4">
              <TasksTab lead={lead} onDirty={setDirty} />
            </TabsContent>
            {/* Mailverlauf managt sein eigenes Zwei-Spalten-Layout + Scrolling —
                daher kein p-4/overflow-auto vom Wrapper (sonst doppeltes Scrollen). */}
            <TabsContent value="mailverlauf" className="flex-1 overflow-hidden p-0">
              <MailverlaufTab lead={lead} />
            </TabsContent>
            <TabsContent value="timeline" className="flex-1 overflow-auto p-4">
              <TimelineTab lead={lead} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
